import React from "react";
import { FormState } from "../type/form-step";
import {
  salaryToAnnual,
  salaryToDisplay,
  SalaryBasis,
} from "../utils/remuneration";

export function SalaryPreferences({
  form,
  update,
}: {
  form: FormState;
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}) {
  const english = form.lang === "en";
  const text = (fr: string, en: string) => (english ? en : fr);
  const basis =
    form.salaryBasis ||
    (form.contractType.includes("freelance") ? "daily" : "annual");
  const unit = form.currency === "EUR" ? "€" : form.currency;
  const id = React.useId();
  return (
    <fieldset className="salary-preferences">
      <legend>{text("Rémunération souhaitée", "Desired compensation")}</legend>
      <div className="salary-controls">
        <label className="field">
          {text("Mode de saisie", "Enter amounts as")}
          <select
            value={basis}
            onChange={(e) =>
              update("salaryBasis", e.target.value as SalaryBasis)
            }
          >
            <option value="annual">
              {text("Annuel brut", "Gross annual")} (k{unit})
            </option>
            <option value="monthly">
              {text("Mensuel brut", "Gross monthly")} ({unit})
            </option>
            <option value="daily">
              {text("TJM freelance HT", "Freelance daily rate, excl. tax")} (
              {unit})
            </option>
          </select>
        </label>
        <label className="field">
          {text("Devise", "Currency")}
          <select
            value={form.currency}
            onChange={(e) => update("currency", e.target.value)}
          >
            {["EUR", "USD", "GBP", "CHF"].map((currency) => (
              <option key={currency}>{currency}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="salary-controls">
        {(["min", "max"] as const).map((bound) => {
          const annualKey = bound === "min" ? "minSalary" : "maxSalary";
          const dailyKey = bound === "min" ? "dailyRateMin" : "dailyRateMax";
          const amount =
            basis === "daily"
              ? form[dailyKey] || ""
              : salaryToDisplay(form[annualKey], basis);
          return (
            <label key={bound} className="field" htmlFor={`${id}-${bound}`}>
              {bound === "min"
                ? text("Minimum", "Minimum")
                : text("Maximum", "Maximum")}{" "}
              ·{" "}
              {basis === "annual"
                ? `k${unit}/${text("an", "year")}`
                : `${unit}/${basis === "daily" ? text("jour HT", "day excl. tax") : text("mois", "month")}`}
              <input
                id={`${id}-${bound}`}
                type="number"
                min="0"
                step={basis === "annual" ? "0.1" : "0.01"}
                value={amount}
                placeholder={
                  basis === "annual"
                    ? bound === "min"
                      ? "40"
                      : "50"
                    : basis === "monthly"
                      ? bound === "min"
                        ? "3333.33"
                        : "4166.67"
                      : bound === "min"
                        ? "400"
                        : "600"
                }
                onChange={(e) =>
                  basis === "daily"
                    ? update(dailyKey, e.target.value)
                    : update(annualKey, salaryToAnnual(e.target.value, basis))
                }
              />
            </label>
          );
        })}
      </div>
      {basis !== "daily" && (
        <div className="salary-equivalent" aria-live="polite">
          <strong>
            {text("Équivalent", "Equivalent")}{" "}
            {basis === "annual"
              ? text("mensuel brut", "gross monthly")
              : text("annuel brut", "gross annual")}
          </strong>
          <span>
            {text("Minimum", "Minimum")} :{" "}
            {salaryToDisplay(
              form.minSalary,
              basis === "annual" ? "monthly" : "annual",
            ) || "—"}{" "}
            {basis === "annual" ? unit : `k${unit}`} ·{" "}
            {text("Maximum", "Maximum")} :{" "}
            {salaryToDisplay(
              form.maxSalary,
              basis === "annual" ? "monthly" : "annual",
            ) || "—"}{" "}
            {basis === "annual" ? unit : `k${unit}`}
          </span>
          <small>
            {text(
              "Conversion automatique sur 12 mois, hors primes.",
              "Automatically converted over 12 months, excluding bonuses.",
            )}
          </small>
        </div>
      )}
      <p className="muted small">
        {text(
          "Le TJM est indépendant du salaire salarié : renseigne-le séparément si tu recherches des missions freelance.",
          "The freelance daily rate is separate from employee pay. Enter it separately if you are seeking freelance work.",
        )}
      </p>
    </fieldset>
  );
}
